import type { MonthlyInvoiceParameters, TariffType, HandlingFeeType } from '../../types/proCalculator';
import { createEmptyParameterValue, calculateFinalValue, HANDLING_FEE } from '../../types/proCalculator';

/**
 * Mock Pro Data Generator
 *
 * Generates mock (a) values from Tauron tariff for Advanced Calculator
 * Uses approximate Tauron 2025 pricing (net values × VAT 1.23)
 *
 * MVP: Only generates (a) values
 * - (b) values are always null (PDF import not available)
 * - (c) values are null by default (user can override)
 * - (d) values are calculated from (a)
 *
 * @param tariff - Tariff type (G11, G12, G12w, G12as, G13, G13s)
 * @param consumption - Monthly consumption in kWh
 * @param handlingFeeType - Handling fee type (REGULATED or COMMERCIAL)
 * @returns MonthlyInvoiceParameters with (a) values populated
 */
export function generateMockBasicValues(
  tariff: TariffType,
  consumption: number,
  handlingFeeType: HandlingFeeType = 'REGULATED'
): MonthlyInvoiceParameters {
  const VAT = 1.23;

  // Get handling fee based on type
  const handlingFeeMonthly = handlingFeeType === 'REGULATED'
    ? HANDLING_FEE.REGULATED
    : HANDLING_FEE.COMMERCIAL_PREMIUM;

  // Tauron 2025 pricing (net values)
  // Source: Approximate values based on Tauron tariff
  const pricing = {
    // Energy Sales
    energyPerKwh: {
      G11: 0.5041,
      G12: 0.4878, // Average of day/night
      G12w: 0.4600,
      G12as: 0.4425,
      G13: 0.4700,
      G13s: 0.4000,
    },
    handlingFeeMonthly, // Based on selected type (REGULATED: 0.47, COMMERCIAL: 14.21)

    // Distribution - Fixed Fees (monthly)
    networkFixedFeeMonthly: 7.02,
    transitionFeeMonthly: 0.08,
    subscriptionFeeMonthly: 0.62,
    powerFeeMonthly: 5.58,

    // Distribution - Variable Fees (per kWh)
    qualityFeePerKwh: 0.0321,
    networkVariableFeePerKwh: {
      G11: 0.2541,
      G12: 0.1754, // Average of day/night
      G12w: 0.1725,
      G12as: 0.1690,
      G13: 0.1800,
      G13s: 0.1600,
    },
    ozeFeePerKwh: 0.0035,
    cogenerationFeePerKwh: 0.0030,
  };

  // Calculate (a) values
  const energyElectricCostNet = consumption * pricing.energyPerKwh[tariff as keyof typeof pricing.energyPerKwh];
  const handlingFeeNet = pricing.handlingFeeMonthly;

  const networkFixedFeeNet = pricing.networkFixedFeeMonthly;
  const transitionFeeNet = pricing.transitionFeeMonthly;
  const qualityFeeNet = consumption * pricing.qualityFeePerKwh;
  const networkVariableFeeNet = consumption * pricing.networkVariableFeePerKwh[tariff as keyof typeof pricing.networkVariableFeePerKwh];
  const ozeFeeNet = consumption * pricing.ozeFeePerKwh;
  const cogenerationFeeNet = consumption * pricing.cogenerationFeePerKwh;
  const subscriptionFeeNet = pricing.subscriptionFeeMonthly;
  const powerFeeNet = pricing.powerFeeMonthly;

  // Create parameter values with (a) populated, (b) and (c) null
  const createParamValue = (netValue: number) => {
    const grossValue = netValue * VAT;
    return {
      valueFromBasic: grossValue,
      valueFromInvoice: null, // MVP: always null
      valueOverridden: null, // User can set this
      valueFinal: grossValue, // (a) is used by default
    };
  };

  return {
    // Energy Sales
    energyElectricCost: createParamValue(energyElectricCostNet),
    handlingFee: createParamValue(handlingFeeNet),

    // Distribution
    networkFixedFee: createParamValue(networkFixedFeeNet),
    transitionFee: createParamValue(transitionFeeNet),
    qualityFee: createParamValue(qualityFeeNet),
    networkVariableFee: createParamValue(networkVariableFeeNet),
    ozeFee: createParamValue(ozeFeeNet),
    cogenerationFee: createParamValue(cogenerationFeeNet),
    subscriptionFee: createParamValue(subscriptionFeeNet),
    powerFee: createParamValue(powerFeeNet),
  };
}

/**
 * Initialize empty monthly parameters
 * Used when no data is available
 */
export function createEmptyMonthlyParameters(): MonthlyInvoiceParameters {
  return {
    energyElectricCost: createEmptyParameterValue(),
    handlingFee: createEmptyParameterValue(),
    networkFixedFee: createEmptyParameterValue(),
    transitionFee: createEmptyParameterValue(),
    qualityFee: createEmptyParameterValue(),
    networkVariableFee: createEmptyParameterValue(),
    ozeFee: createEmptyParameterValue(),
    cogenerationFee: createEmptyParameterValue(),
    subscriptionFee: createEmptyParameterValue(),
    powerFee: createEmptyParameterValue(),
  };
}

/**
 * Update monthly parameters when user changes tariff, consumption, or handling fee type
 * Regenerates (a) values while preserving (c) user overrides
 *
 * @param currentParams - Current monthly parameters
 * @param tariff - New tariff type
 * @param consumption - New consumption value
 * @param handlingFeeType - Handling fee type (REGULATED or COMMERCIAL)
 * @returns Updated monthly parameters with new (a) values
 */
export function updateMonthlyParameters(
  currentParams: MonthlyInvoiceParameters,
  tariff: TariffType,
  consumption: number,
  handlingFeeType: HandlingFeeType = 'REGULATED'
): MonthlyInvoiceParameters {
  // Generate new (a) values
  const newParams = generateMockBasicValues(tariff, consumption, handlingFeeType);

  // Preserve (c) user overrides
  const parameterKeys = Object.keys(currentParams) as Array<keyof MonthlyInvoiceParameters>;
  parameterKeys.forEach((key) => {
    const currentParam = currentParams[key];
    const newParam = newParams[key];

    // Keep user override (c) if it exists
    if (currentParam.valueOverridden !== null) {
      newParam.valueOverridden = currentParam.valueOverridden;
      newParam.valueFinal = calculateFinalValue(
        newParam.valueFromBasic,
        newParam.valueFromInvoice,
        newParam.valueOverridden
      );
    }
  });

  return newParams;
}
